/// <amd-module name="Test/TestPlansHubRefresh/TestPlanClonePanel/Component/TestPlanClonePanelConnectedView" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { TestPlanClonePanelActions } from "Test/TestPlansHubRefresh/TestPlanClonePanel/Redux/TestPlanClonePanelActions";
import { INode } from "Test/TestPlansHubRefresh/TestPlanClonePanel/Redux/TestPlanClonePanelContracts";
import { ITestPlanClonePanelAwareState } from "Test/TestPlansHubRefresh/TestPlanClonePanel/Redux/TestPlanClonePanelContracts";
import { TestPlanClonePanelModule } from "Test/TestPlansHubRefresh/TestPlanClonePanel/Redux/TestPlanClonePanelModule";
import * as Resources from "Test/TestPlansHubRefresh/TestPlanClonePanel/Resources";
import { TestPlanClonePanelConstants } from "Test/TestPlansHubRefresh/TestPlanClonePanel/TestPlanClonePanelConstants";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { Components, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { CustomPanel, PanelCloseButton, PanelContent, PanelFooter } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField, TextFieldWidth } from "VSSUI/TextField";

export interface ITestPlanClonePanelOwnProps extends IVssComponentProperties {
    showPanel: ObservableValue<boolean>;
}

export interface ITestPlanClonePanelViewProps {
    planId: number; // plan Id
    planAreaPath: string; // plan area path
    planIteration: string; // plan iteration path
    areaPathNode: INode; // Area path details
    iterationPathNode: INode; // Iteration details
    cloneInProgress: boolean; // if clone is in progress
    modulesLoaded: boolean; // if modules are being loaded
    newPlanId: number; // new plan Id which is cloned
    opId: number | undefined; // opId associated with the new clone
    mandatoryFieldUpdated: boolean; // if all the mandatory field in the Panel is updated
}

export interface ITestPlanClonePanelViewDispatchProps {
    copyTestPlan: (sourcePlanId: number, desPlanName: string, desAreaPath: string, desIteration: string, isDeep: boolean) => void; // dispatch copyTestPlan action
    updateMandatoryField: (update: boolean) => void; // dispatch updateMandatoryField action
}

export class TestPlansClonePanelView extends VssComponent<ITestPlanClonePanelViewProps & ITestPlanClonePanelOwnProps & ITestPlanClonePanelViewDispatchProps> {
    private _projectId: string;
    private _newPlan: ObservableValue<string>;
    private _selectedAreaPath: string = "";
    private _selectedIteration: string = "";
    private _selectedRadioButton = new ObservableValue<string>("");
    constructor(props: ITestPlanClonePanelViewProps & ITestPlanClonePanelOwnProps & ITestPlanClonePanelViewDispatchProps, context?: IVssComponentContext) {
        super(props);
        const pageContext: IVssPageContext = context!.pageContext;
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }
        this._newPlan = new ObservableValue<string>("");

        this._selectedAreaPath = this.props.planAreaPath;
        this._selectedIteration = this.props.planIteration;
    }

    public render(): JSX.Element {
        let panelContent = <Spinner className="spinner-panel" size={SpinnerSize.large} />;

        if (this.props.areaPathNode && this.props.iterationPathNode && this.props.modulesLoaded) {
            panelContent = (
                <div className="test-plans-clone-panel-content">
                    <FormItem label={Resources.NewTestPlan} className="test-plan-name">
                        <TextField
                            value={this._newPlan}
                            onChange={(e, newValue) => {
                                this._newPlan.value = newValue;
                                this.enableCreateButtonIfRequired();
                            }}
                            autoFocus={true}
                            placeholder={Resources.NewTestPlanDescription}
                            width={TextFieldWidth.auto}
                        />
                    </FormItem>
                    <LegacyComponent
                        modules={["TestManagement/Scripts/Shared/AreaPathAndIterationPicker"]}
                        wrappedType="area-path-and-iteration-picker"
                        wrappedProps={{
                            areaPathNode: this.props.areaPathNode,
                            iterationNode: this.props.iterationPathNode,
                            projectId: this._projectId,
                            areaPathNodeSelected: this.areaPathNodeSelected.bind(this),
                            iterationNodeSelected: this.iterationNodeSelected.bind(this),
                            selectedAreaPath: this.props.planAreaPath,
                            selectedIteration: this.props.planIteration
                        }}
                    />
                    <div className="clone-option-component">
                        <RadioButtonGroup
                            onSelect={selectedId => (this._selectedRadioButton.value = selectedId)}
                            selectedButtonId={this._selectedRadioButton}
                        >
                            <RadioButton
                                key={TestPlanClonePanelConstants.referRadioButtonType}
                                id={TestPlanClonePanelConstants.referRadioButtonType}
                                ariaLabelledBy="refer-test-cases"
                            >
                                <div className="clone-radio-label" id="refer-test-cases">
                                    <div className="clone-radio-primary title-xs">{Resources.ReferTestCases}</div>
                                    <div className="clone-radio-secondary">{Resources.ReferDescription}</div>
                                </div>
                            </RadioButton>
                            <div className="clone-separator" />
                            <RadioButton
                                key={TestPlanClonePanelConstants.cloneRadioButtonType}
                                id={TestPlanClonePanelConstants.cloneRadioButtonType}
                                ariaLabelledBy="clone-test-cases"
                            >
                                <div className="clone-radio-label" id="clone-test-cases">
                                    <div className="clone-radio-primary title-xs">{Resources.CloneTestCases}</div>
                                    <div className="clone-radio-secondary">{Resources.CloneDescription}</div>
                                </div>
                            </RadioButton>
                            <div className="clone-separator" />
                        </RadioButtonGroup>
                    </div>
                </div>
            );
        }

        return (
            <DynamicModuleLoader modules={[TestPlanClonePanelModule]}>
                <CustomPanel lightDismiss={false} onDismiss={() => (this.props.showPanel.value = false)}>
                    <CustomHeader>
                        <HeaderTitleArea>
                            <div className="clone-panel-header title-m">{Resources.CopyTestPlan}</div>
                        </HeaderTitleArea>
                        <PanelCloseButton onDismiss={() => (this.props.showPanel.value = false)} />
                    </CustomHeader>
                    <PanelContent>
                        <div className="clone-test-plan-panel">{panelContent}</div>
                    </PanelContent>
                    <PanelFooter>
                        <div className="clone-panel-footer">
                            <Button
                                className="panel-create-button"
                                text={Resources.Create}
                                primary={true}
                                disabled={this.props.cloneInProgress || !this.props.mandatoryFieldUpdated}
                                onClick={() => {
                                    this.props.copyTestPlan(
                                        this.props.planId,
                                        this._newPlan.value,
                                        this._selectedAreaPath,
                                        this._selectedIteration,
                                        this._shouldDeepClone()
                                    );
                                }}
                            />
                            {this.props.cloneInProgress && <Spinner className="spinner-footer" size={SpinnerSize.medium} />}
                        </div>
                    </PanelFooter>
                </CustomPanel>
            </DynamicModuleLoader>
        );
    }

    public shouldComponentUpdate(nextProps: ITestPlanClonePanelViewProps) {
        // if opId is being created ie, the clone has been initiated, close  the panel
        if (nextProps.opId && nextProps.opId > 0) {
            this.props.showPanel.value = false;
        }
        return true;
    }

    public areaPathNodeSelected(path: string) {
        this._selectedAreaPath = path;
        this.enableCreateButtonIfRequired();
    }

    public iterationNodeSelected(path: string) {
        this._selectedIteration = path;
        this.enableCreateButtonIfRequired();
    }

    public enableCreateButtonIfRequired() {
        const update = this._selectedAreaPath && this._selectedIteration && this._newPlan.value ? true : false;
        if (update != this.props.mandatoryFieldUpdated) {
            this.props.updateMandatoryField(update);
        }
    }

    private _shouldDeepClone(): boolean {
        return this._selectedRadioButton.value === TestPlanClonePanelConstants.cloneRadioButtonType ? true : false;
    }
}

function mapStateToProps(state: ITestPlanClonePanelAwareState): Partial<ITestPlanClonePanelViewProps> {
    return {
        planId: state.testHubRefreshState.testPlanId,
        planAreaPath: state.testHubRefreshState.testPlanAreaPath,
        planIteration: state.testHubRefreshState.testPlanIteration,
        areaPathNode: state.testPlanClonePanelState ? state.testPlanClonePanelState.areaPathNode : undefined,
        iterationPathNode: state.testPlanClonePanelState ? state.testPlanClonePanelState.iterationNode : undefined,
        opId: state.testPlanClonePanelState ? state.testPlanClonePanelState.opId : -1,
        newPlanId: state.testPlanClonePanelState ? state.testPlanClonePanelState.newPlanId : -1,
        cloneInProgress: state.testPlanClonePanelState ? state.testPlanClonePanelState.cloneInProgress : false,
        modulesLoaded: state.testPlanClonePanelState ? state.testPlanClonePanelState.modulesLoaded : false,
        mandatoryFieldUpdated: state.testPlanClonePanelState ? state.testPlanClonePanelState.mandatoryFieldUpdated : false
    };
}

function mapDispatchToProps(dispatch: Dispatch): ITestPlanClonePanelViewDispatchProps {
    return {
        copyTestPlan: (sourcePlanId: number, destPlanName: string, destAreaPath: string, destIteration: string, isDeepClone: boolean) =>
            dispatch(TestPlanClonePanelActions.copyTestPlan(sourcePlanId, destPlanName, destAreaPath, destIteration, isDeepClone)),
        updateMandatoryField: (update: boolean) => dispatch(TestPlanClonePanelActions.mandatoryFieldUpdated(update))
    };
}

export const TestPlanClonePanelConnectedView = connect(
    mapStateToProps as ((state: ITestPlanClonePanelAwareState) => ITestPlanClonePanelViewProps),
    mapDispatchToProps
)(TestPlansClonePanelView);

Components.add("clone-test-plan-panel", TestPlanClonePanelConnectedView);
